'use client'
import { useSetValueFromForm } from '@/hooks/Form'
import { Icon } from '@/icons'
import cn from 'classnames'
import React, {
  forwardRef,
  KeyboardEvent,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { IMaskInput } from 'react-imask'
import styles from './Input.module.scss'

export type InputProps<TName> = {
  value?: string
  name?: TName
  onChange?: (value: string, name?: TName) => void
  onFocus?: () => void
  onBlur?: (value: string) => void
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void
  placeholder?: string
  error?: string
  mask?: string | NumberConstructor | RegExp
  type?: 'text' | 'password' | 'email'
  label?: string
  className?: string
  readonly?: boolean
  disabled?: boolean
  leftIcon?: any // @TODO type IconName
  autocomplete?: boolean
  autoFocus?: boolean
  permanentPlaceholder?: boolean
  testId?: string
  withoutClearButton?: boolean
  onClear?: () => void
}
export const Input = forwardRef(
  <TName,>(
    {
      value: outsideValue,
      onChange = (_value) => {},
      onFocus,
      onBlur,
      placeholder,
      name,
      error,
      mask,
      type = 'text',
      label = '',
      className = '',
      readonly = false,
      leftIcon = null,
      autocomplete = true,
      permanentPlaceholder = false,
      testId = 'inputContainer',
      withoutClearButton = false,
      onClear,
      ...props
    }: InputProps<TName>,
    ref: React.Ref<HTMLInputElement>
  ) => {
    const [localValue, setLocalValue] = useState<string>('')

    const value = useMemo(
      () => outsideValue ?? localValue,
      [outsideValue, localValue]
    )

    const change = useCallback(
      (value) => {
        if (outsideValue == null) {
          setLocalValue(value)
        }
        onChange(value, name)
      },
      [onChange, outsideValue, name]
    )

    const _onChange = useCallback(
      (event) => {
        change(event.target.value)
      },
      [change]
    )

    const _onPaste = useCallback(
      (event) => {
        setTimeout(() => {
          change(event.target.value)
        })
      },
      [change]
    )

    const [focused, setFocused] = useState(false)

    const focus = useCallback(() => {
      setFocused(true)
      if (onFocus != null) {
        onFocus()
      }
    }, [onFocus])

    const blur = useCallback(
      (event) => {
        setFocused(false)
        if (onBlur != null) {
          onBlur(event.target.value)
        }
      },
      [onBlur]
    )

    useSetValueFromForm(name, change)

    const clearInput = useCallback(() => {
      setLocalValue('')
      onChange('', name)

      if (onClear != null) {
        onClear()
      }
    }, [onChange, name])

    const [passwordView, setPasswordView] = useState(false)
    const togglePasswordView = useCallback(() => {
      setPasswordView((value) => !value)
    }, [])

    const isPassword = useMemo(() => type === 'password', [type])

    const _type = useMemo(() => {
      if (type === 'password' && passwordView) {
        return 'text'
      }
      return type
    }, [type, passwordView])

    return (
      <div className={cn(styles.container, className)} data-testid={testId}>
        <div
          className={cn(styles.containerInput, {
            [styles.isReadonly]: readonly,
            [styles.isError]: error,
          })}
        >
          {label ? <label className={styles.label}>{label}</label> : null}

          <div
            className={cn(styles.body, {
              [styles.isFocused]: focused,
            })}
          >
            {leftIcon ? (
              <div className={styles.leftIcon}>{leftIcon}</div>
            ) : null}
            <IMaskInput
              type={_type}
              value={value}
              onChange={_onChange}
              inputRef={ref}
              className={cn(styles.input, {
                [styles.inputLeftIcon]: leftIcon,
                [styles.withPermanentPlaceholder]:
                  permanentPlaceholder && (focused || value.length !== 0),
              })}
              placeholder={!permanentPlaceholder ? placeholder : ''}
              onPaste={_onPaste}
              mask={mask}
              name={name}
              readOnly={readonly}
              autoComplete={autocomplete ? 'on' : 'off'}
              onFocus={focus}
              onBlur={blur}
              {...props}
            />
            {permanentPlaceholder ? (
              <div
                className={cn(styles.placeholder, {
                  [styles.isMinimize]: value.length > 0 || focused,
                })}
              >
                {placeholder}
              </div>
            ) : null}
            {isPassword ? (
              <button
                type="button"
                onClick={togglePasswordView}
                className={styles.icon}
                data-testid="inputTogglePasswordView"
              >
                {passwordView ? <Icon name="eyeOn" /> : <Icon name="eyeOff" />}
              </button>
            ) : null}
            {!withoutClearButton && !isPassword && !readonly ? (
              <button
                type="button"
                onClick={clearInput}
                className={cn(styles.icon, {
                  [styles.isHidden]: value.length === 0,
                })}
              >
                <Icon name="clear" className={styles.iconClear} />
              </button>
            ) : null}
          </div>
        </div>
        {error ? <div className={styles.error}>{error}</div> : null}
      </div>
    )
  }
)

Input.displayName = 'Input'
